import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { divisionsAPI } from '../services/divisionsApi';
import { adminTeamsAPI } from '../services/adminApi';
import { CANTON_VARIATIONS } from '../services/apiRegistry';
import { CANTONS, NEW_CANTONS } from '../utils/cantons';
import { useAdminSeasonPhaseFilter } from '../hooks/useAdminSeasonPhaseFilter';
import AdminSeasonPhaseFilters from './AdminSeasonPhaseFilters';

/** Same canton matching as AdminCantonDetail / admin teams API filter — avoids undercount from summing division.team_count. */
function teamMatchesOverviewCanton(teamCanton, overviewCode) {
  if (teamCanton == null || teamCanton === '') return false;
  const variations = CANTON_VARIATIONS[overviewCode] || [overviewCode];
  const t = String(teamCanton).trim();
  return variations.some((v) => {
    const vv = String(v).trim();
    return t === vv || t.toLowerCase() === vv.toLowerCase();
  });
}

/**
 * Same basis as GET /admin/teams?canton=…: playing canton is the division's canton when assigned;
 * otherwise registration/season canton on the team (unassigned).
 */
function getPlayingCantonForOverview(team, divisionById) {
  const divId = team.division_id;
  if (divId && divisionById[divId] && divisionById[divId].canton != null && divisionById[divId].canton !== '') {
    return divisionById[divId].canton;
  }
  return team.canton;
}

const CantonOverviewContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  padding: 2rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #043663 0%, #043663 100%);
  color: white;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const TitleSection = styled.div`
  flex: 1;
  width: 100%;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  opacity: 0.9;
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 300px;
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
  }
`;

const SeasonPhaseWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;

  label {
    color: rgba(255, 255, 255, 0.9) !important;
  }

  select {
    min-width: 160px;
    background: rgba(255, 255, 255, 0.95);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    align-items: stretch;
  }
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  font-size: 0.875rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  backdrop-filter: blur(10px);

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StatsSummary = styled.div`
  display: flex;
  gap: 1.5rem;
  font-size: 0.875rem;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 1rem;
    font-size: 0.8rem;
  }
`;

// eslint-disable-next-line no-unused-vars
const CantonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  padding: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1rem;
  }
`;

// eslint-disable-next-line no-unused-vars
const CantonCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px 0 rgba(0, 0, 0, 0.1);
    border-color: #043663;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #043663, #043663);
  }
`;

// eslint-disable-next-line no-unused-vars
const CantonHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

// eslint-disable-next-line no-unused-vars
const CantonFlag = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: #043663;
`;

// eslint-disable-next-line no-unused-vars
const CantonInfo = styled.div`
  flex: 1;
`;

// eslint-disable-next-line no-unused-vars
const CantonName = styled.h3`
  margin: 0 0 0.25rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

// eslint-disable-next-line no-unused-vars
const CantonCode = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

// eslint-disable-next-line no-unused-vars
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`;

// eslint-disable-next-line no-unused-vars
const StatItem = styled.div`
  text-align: center;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
`;

// eslint-disable-next-line no-unused-vars
const StatNumber = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #043663;
  margin-bottom: 0.25rem;
`;

// eslint-disable-next-line no-unused-vars
const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

// eslint-disable-next-line no-unused-vars
const DivisionPreview = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
`;

// eslint-disable-next-line no-unused-vars
const DivisionPreviewTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
  color: #374151;
  font-weight: 600;
`;

// eslint-disable-next-line no-unused-vars
const DivisionList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

// eslint-disable-next-line no-unused-vars
const DivisionTag = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: #dbeafe;
  color: #043663;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  padding: 3rem;
  text-align: center;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  padding: 3rem;
  text-align: center;
  color: #d52b1e;
`;

const EmptyContainer = styled.div`
  padding: 3rem;
  text-align: center;
  color: #6b7280;
`;

// Table styled components
const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  background: white;
  
  @media (max-width: 768px) {
    margin: 0 -1rem;
    padding: 0 1rem;
  }
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const CantonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  min-width: 700px;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const TableHeader = styled.thead`
  background: #f8fafc;
`;

const HeaderRow = styled.tr`
  border-bottom: 2px solid #e5e7eb;
`;

const HeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: #374151;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.8rem;
  }
`;

const CantonCell = styled(TableCell)`
  font-weight: 500;
`;

const CantonInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CantonEmoji = styled.span`
  font-size: 1.25rem;
  width: 2rem;
  text-align: center;
`;

const CantonDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const CantonNameRow = styled.div`
  font-weight: 600;
  color: #1f2937;
`;

const CantonCodeRow = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
`;

const ViewButton = styled.button`
  background: #043663;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #2563eb;
  }
`;

const AdminCantonOverview = ({ onCantonSelect, selectedSeason: propSelectedSeason, onSeasonChange }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const seasonPhase = useAdminSeasonPhaseFilter({
    allowAll: true,
    autoDefault: true,
    preferredPhaseId: propSelectedSeason || '',
  });

  const selectedSeason = seasonPhase.seasonId;

  // Keep parent seasonId in sync (divisions detail views rely on it)
  useEffect(() => {
    if (!onSeasonChange) return;
    const next = selectedSeason || '';
    if (String(propSelectedSeason || '') === String(next)) return;
    onSeasonChange(next);
  }, [selectedSeason, onSeasonChange, propSelectedSeason]);

  // Fetch all divisions grouped by canton - season-aware
  const { data: divisionsData, isLoading: divisionsLoading, error: divisionsError } = useQuery(
    ['cantonDivisions', selectedSeason],
    () => divisionsAPI.getAll({ season_id: selectedSeason || undefined, limit: 1000 }),
    {
      enabled: true,
      refetchInterval: 30000
    }
  );

  // Fetch all teams for stats - season-aware
  const { data: teamsData, isLoading: teamsLoading } = useQuery(
    ['cantonTeams', selectedSeason],
    () => adminTeamsAPI.getAll({ season_id: selectedSeason || undefined, limit: 5000 }),
    {
      enabled: true,
      refetchInterval: 30000
    }
  );

  if (divisionsLoading || teamsLoading) {
    return (
      <CantonOverviewContainer>
        <LoadingContainer>Loading canton data...</LoadingContainer>
      </CantonOverviewContainer>
    );
  }

  if (divisionsError) {
    return (
      <CantonOverviewContainer>
        <ErrorContainer>Error loading canton data: {divisionsError.message}</ErrorContainer>
      </CantonOverviewContainer>
    );
  }

  const divisions = divisionsData?.data?.divisions || [];
  const divisionById = {};
  divisions.forEach((d) => {
    divisionById[d.id] = d;
  });

  // Filter out disabled teams - use season_is_active if available (season-aware), otherwise use is_active
  const teams = (teamsData?.data?.teams || []).filter(team => {
    const isTeamActive = team.season_is_active !== undefined ? team.season_is_active : team.is_active;
    return isTeamActive !== false; // Include teams that are active (true or undefined/null)
  });

  // Group divisions by canton
  const cantonStats = {};
  
  divisions.forEach(division => {
    if (!cantonStats[division.canton]) {
      cantonStats[division.canton] = {
        canton: division.canton,
        divisions: [],
        totalTeams: 0,
        totalMembers: 0
      };
    }
    cantonStats[division.canton].divisions.push(division);
  });

  // Create list of active cantons (original + new consolidated, excluding old ones)
  const activeCantons = [
    // Original cantons (excluding the ones that were consolidated)
    ...CANTONS.filter(canton => !['AR', 'AI', 'BL', 'BS', 'NW', 'OW', 'UR', 'GR'].includes(canton.code)),
    // New consolidated cantons
    ...NEW_CANTONS
  ];

  // Per-row counts: playing canton (division when assigned), same as canton detail / admin teams canton filter — not raw team.canton alone.
  const teamCountsByOverviewCanton = {};
  activeCantons.forEach((c) => {
    teamCountsByOverviewCanton[c.code] = { totalTeams: 0, totalMembers: 0 };
  });
  teams.forEach((team) => {
    const playingCanton = getPlayingCantonForOverview(team, divisionById);
    activeCantons.forEach((c) => {
      if (teamMatchesOverviewCanton(playingCanton, c.code)) {
        teamCountsByOverviewCanton[c.code].totalTeams += 1;
        teamCountsByOverviewCanton[c.code].totalMembers += parseInt(team.member_count || 0, 10);
      }
    });
  });

  const sumRowTeams = activeCantons.reduce(
    (s, c) => s + (teamCountsByOverviewCanton[c.code]?.totalTeams || 0),
    0
  );
  if (sumRowTeams !== teams.length) {
    console.warn('[AdminCantonOverview] Canton row team sum differs from loaded team list — some teams may not match any overview canton, or limit truncation.', {
      seasonId: selectedSeason,
      teamsInList: teams.length,
      sumOfCantonRows: sumRowTeams
    });
  }

  // Create complete canton list with active Swiss cantons
  const cantonList = activeCantons.map(canton => {
    const stats = cantonStats[canton.code] || {
      canton: canton.code,
      divisions: [],
      totalTeams: 0,
      totalMembers: 0
    };
    const fromTeams = teamCountsByOverviewCanton[canton.code] || { totalTeams: 0, totalMembers: 0 };

    return {
      ...canton,
      ...stats,
      totalTeams: fromTeams.totalTeams,
      totalMembers: fromTeams.totalMembers,
      divisions: stats.divisions || []
    };
  });

  // Sort cantons alphabetically by name
  const sortedCantons = cantonList.sort((a, b) => a.name.localeCompare(b.name));

  // Filter cantons based on search term
  const filteredCantons = sortedCantons.filter(canton => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      canton.name.toLowerCase().includes(searchLower) ||
      canton.code.toLowerCase().includes(searchLower)
    );
  });

  // Header totals: same basis as rows (sum of matched teams)
  const totalTeams = sumRowTeams;
  const totalMembers = teams.reduce((sum, team) => sum + parseInt(team.member_count || 0, 10), 0);
  const activeCantonsCount = cantonList.filter(canton => canton.totalTeams > 0).length;

  if (cantonList.length === 0) {
    return (
      <CantonOverviewContainer>
        <EmptyContainer>No cantons found. Create some divisions first.</EmptyContainer>
      </CantonOverviewContainer>
    );
  }

  return (
    <CantonOverviewContainer>
      <Header>
        <HeaderContent>
          <TitleSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', marginBottom: '1rem' }}>
              <div>
                <Title>Canton Overview</Title>
                <Subtitle>Select a canton to view detailed divisions and team statistics</Subtitle>
              </div>
              <SeasonPhaseWrap>
                <AdminSeasonPhaseFilters
                  campaignId={seasonPhase.campaignId}
                  seasonId={seasonPhase.seasonId}
                  seasonOptions={seasonPhase.seasonOptions}
                  phaseOptions={seasonPhase.phaseOptions}
                  onCampaignChange={seasonPhase.onCampaignChange}
                  onPhaseChange={seasonPhase.onPhaseChange}
                  allowAll
                  disabled={seasonPhase.isLoading}
                />
              </SeasonPhaseWrap>
            </div>
          </TitleSection>
          <SearchSection>
            <SearchInput
              type="text"
              placeholder="Search cantons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <StatsSummary>
              <span>{totalTeams} teams</span>
              <span>{totalMembers} players</span>
              <span>{activeCantonsCount} active cantons</span>
            </StatsSummary>
          </SearchSection>
        </HeaderContent>
      </Header>

      <TableWrapper>
        <CantonTable>
          <TableHeader>
            <HeaderRow>
              <HeaderCell>#</HeaderCell>
              <HeaderCell>Canton</HeaderCell>
              <HeaderCell>Teams</HeaderCell>
              <HeaderCell>Players</HeaderCell>
              <HeaderCell>Divisions</HeaderCell>
              <HeaderCell>Active Divisions</HeaderCell>
              <HeaderCell>Action</HeaderCell>
            </HeaderRow>
          </TableHeader>
          <TableBody>
            {filteredCantons.map((canton, index) => (
              <TableRow key={canton.code} onClick={() => onCantonSelect(canton.code)}>
                <TableCell style={{ textAlign: 'center', fontWeight: '600', color: '#6b7280' }}>
                  {index + 1}
                </TableCell>
                <CantonCell>
                  <CantonInfoRow>
                    <CantonEmoji>{canton.emoji}</CantonEmoji>
                    <CantonDetails>
                      <CantonNameRow>{canton.name}</CantonNameRow>
                      <CantonCodeRow>{canton.code}</CantonCodeRow>
                    </CantonDetails>
                  </CantonInfoRow>
                </CantonCell>
                <TableCell>{canton.totalTeams}</TableCell>
                <TableCell>{canton.totalMembers}</TableCell>
                <TableCell>{canton.divisions.length}</TableCell>
                <TableCell>{canton.divisions.filter(d => d.team_count > 0).length}</TableCell>
                <TableCell>
                  <ViewButton>View Details</ViewButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </CantonTable>
      </TableWrapper>
    </CantonOverviewContainer>
  );
};

export default AdminCantonOverview;
